import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpHeaders, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { AuthService } from 'src/app/core/services/auth.service';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { ToastController } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import jwt_decode from 'jwt-decode';


@Injectable()
export class JwtInterceptor implements HttpInterceptor {

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject(null);

    constructor(private authService: AuthService, private toastCtrl: ToastController) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Check if we need additional token logic or not
        if (this.isInBlockedList(request.url)) {
            return next.handle(request);
        } else {
            // Proactively check if the token is expired before making the request
            if (this.authService.currentAccessToken && this.isTokenExpired() && !this.isRefreshingToken) {
                console.log('Token expired before request, refreshing token');
                // Token is expired, refresh it before proceeding with the request
                return this.handleExpiredToken(request, next);
            }
            
            return next.handle(this.addToken(request)).pipe(
                catchError((err: HttpErrorResponse) => {

                    //If we encounter a 400 or 401 error from the request, it means the token is invalid
                    if (err instanceof HttpErrorResponse) {
                        switch (err.status) {
                            case 400:
                                return this.handle400Error(err);
                            case 401:
                                return this.handle401Error(request, next);
                            case 403:
                                return this.handle401Error(request, next);
                            case 404:
                                // Check if 404 might be due to expired token
                                if (this.isTokenExpired()) {
                                    console.log('404 error with expired token, attempting refresh');
                                    return this.handle401Error(request, next);
                                }
                                return throwError(() => err);
                            default:
                                return throwError(() => err);
                        }
                    } else {
                        return throwError(() => err);
                    }
                })
            )
        }
    }
    
    /**
     * Handle expired token before sending request
     */
    private handleExpiredToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.tokenSubject.next(null);
        this.isRefreshingToken = true;
        this.authService.currentAccessToken = null;

        // Get a new access token
        return this.authService.getNewAccessToken().pipe(
            switchMap((data: any) => {
                console.log("Proactively fetched new access token");
                if (data) {
                    // Store the new token
                    const accessToken = data.accessToken;
                    return this.authService.storeAccessToken(data).pipe(
                        switchMap(_ => {
                            this.tokenSubject.next(accessToken);
                            // Continue with the request using the new token
                            return next.handle(this.addToken(request));
                        })
                    );
                } else {
                    console.log("Problem with fetching new token proactively, logging out");
                    return this.handle400Error({ message: 'Failed to refresh token' });
                }
            }),
            finalize(() => {
                this.isRefreshingToken = false;
            }),
            catchError(err => {
                return this.handle400Error(err);
            })
        );
    }


    // We are not just authorized, we couldn't refresh token
    // or something else 
    private handle400Error(err): Observable<any> {
        console.log("handle 400 error");
        // Potentially check the exact error reason for the 400
        // then log out the user automatically
        this.toastCtrl.create({
            message: 'Logged out due to authentication mismatch',
            duration: 2000
        }).then(toast => toast.present());
        this.authService.doLogout();
        return of(null);
    }

    // Indicates our access token is invalid, try to load a new one
    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        console.log("handle 401 error");
        // Check if another call is already using the refresh logic
        if (!this.isRefreshingToken) {

            // Set to null so other requests will wait
            // until we got a new token!
            this.tokenSubject.next(null);
            this.isRefreshingToken = true;
            this.authService.currentAccessToken = null;

            // First, get a new access token
            return this.authService.getNewAccessToken().pipe(
                switchMap((data: any) => {
                    console.log("fetch new access token");
                    if (data) {
                        // Store the new token
                        const accessToken = data.accessToken;
                        return this.authService.storeAccessToken(data).pipe(
                            switchMap(_ => {
                                // Use the subject so other calls can continue with the new token
                                this.tokenSubject.next(accessToken);

                                // Perform the initial request again with the new token
                                return next.handle(this.addToken(request));
                            })
                        );
                    } else {
                        console.log("problem with fetch new token, logging out");
                        // No new token, logout the user
                        return this.handle400Error({ message: 'Failed to refresh token' });
                    }
                }),
                finalize(() => {
                    // Unblock the token reload logic when everything is done
                    this.isRefreshingToken = false;
                }),
                catchError(err => this.handle400Error(err))
            );
        } else {
            // "Queue" other calls while we load a new token
            return this.tokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                    // Perform the request again now that we got a new token!
                    return next.handle(this.addToken(request));
                })
            );
        }
    }


    private isInBlockedList(url: string) {
        if (url.indexOf('/auth/authenticate') >= 0 || url.indexOf('/auth/refresh') >= 0 || url.indexOf('/auth/mobile/resetPassword') >= 0 ||
        url.indexOf('/auth/mobile/forgotPassword') >= 0 || url.indexOf('/auth/contactRegisterUrl') >= 0 ) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Checks if a JWT token is expired
     * @returns boolean indicating if the token is expired
     */
    private isTokenExpired(): boolean {
        const token = this.authService.currentAccessToken;
        if (!token) {
            return true;
        }
        
        try {
            const decoded: any = jwt_decode(token);
            const currentTime = Date.now() / 1000; // Convert to seconds
            
            // Add a buffer of 30 seconds to handle any timing issues
            return decoded.exp < (currentTime + 30);
        } catch (error) {
            console.error('Error decoding token:', error);
            return true;
        }
    }

    private addToken(req: HttpRequest<any>) {
        // Check if token is expired before adding it to the request
        if (this.isTokenExpired()) {
            console.log('Token expired, attempting to refresh before request');
            // We'll let the 401 handler deal with getting a new token
            // This way we don't have to modify the main intercept flow
        }
        
        return req.clone({
            headers: new HttpHeaders({
                Authorization: `Bearer ${this.authService.currentAccessToken}`
            })
        });
    }
}
